import React, { useState, useEffect, useRef, useMemo } from 'react';

const DropdownMenu = ({ trigger, options, onSelect, disabledOptions = [], className = '', classNameToggle = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const calculateMenuPosition = useMemo(() => {
    if (!isOpen || !dropdownRef.current || !menuRef.current) return {};

    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // По умолчанию меню открывается снизу по левому краю
    let style = {
      position: 'absolute',
      left: 0,
      top: dropdownRect.height,
    };

    // Если не хватает места снизу, открываем сверху
    const spaceBelow = viewportHeight - dropdownRect.bottom;
    const spaceAbove = dropdownRect.top;
    if (menuRect.height > spaceBelow && spaceAbove > menuRect.height) {
      style.top = -menuRect.height;
    }

    // Если не хватает места справа, сдвигаем влево
    const spaceRight = viewportWidth - dropdownRect.right - scrollbarWidth;
    if (menuRect.width > spaceRight) {
      style.left = dropdownRect.left - (menuRect.width - dropdownRect.width);
    }

    // Если не хватает места слева, сдвигаем вправо
    if (style.left < 0) {
      style.left = dropdownRect.right - menuRect.width;
    }

    return style;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setMenuStyle(calculateMenuPosition);
      const handleResize = () => {
        setMenuStyle(calculateMenuPosition);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, calculateMenuPosition]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option.disabled) return;
    onSelect(option);
    setIsOpen(false);
  };

  const isOptionDisabled = (option) => {
    return disabledOptions.some(id => id === option.id || (option.id && option.id === id));
  };

  return (
    <div className={`dropdown-menu-container ${className}`} ref={dropdownRef}>
      <div 
        className={`dropdown-toggle ${classNameToggle}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {trigger}
      </div>
      
      {isOpen && (
        <ul 
          className="dropdown-menu-list" 
          ref={menuRef}
          style={{
            ...menuStyle,
            minWidth: '200px',
            maxWidth: '300px',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {options.map((option, index) => {
            const disabled = isOptionDisabled(option) || option.disabled;
            return (
              <li 
                key={option.id || index}
                className={`dropdown-menu-item${disabled ? ' disabled' : ''}`}
                onClick={() => handleSelect(option)}
                style={{
                  color: disabled ? '#777777' : 'inherit',
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
              >
                {option.name}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DropdownMenu;
